import {ApplicationInitStatus, Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {config as appConfig} from 'app/core/app-config'
@Injectable({
  providedIn: 'root'
})
export class GtmService {

  /**
   * Indicates whenever GTM was initialized
   */
  private initialized = false;
  private gtagManagerEtab;

  /**
   * Stores pushes to dataLayer that will be added to dataLayer once the app will be initialized
   */
  private postponedPushes = [];

  private domDocument: Document;
  private isEnabled: Boolean= true;
  constructor(
    
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) domDocument
  ) {
    this.domDocument = domDocument;
  }

  /**
   * Push any object do GTM dataLayer
   *
   * @param obj - object that will be pushed to dataLayer
   */
  public push(obj: {[key: string]: any}): void {
    if (this.initialized) {
      this.pushToDataLayer(obj);
    } else {
      this.postponedPushes.push(obj);
    }
  }

  /**
   * Waits for app initialization and then initialize module
   */
  public postponeInitialization(code : string = null): void {
    this.gtagManagerEtab = code;
      this.init();
      console.log("Init Gtm");
  }

  /**
   * Initialize service - create dataLayer and setup necessary HTML code
   */
  private init(): void {

    if (!appConfig.GtmId) {
      throw new Error('gtmId must be provided');
    }
    if (!this.initialized && this.isEnabled !== false) {
      this.initDataLayer();
      this.pushToDataLayer({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });
      this.appendGtmTags();
      this.pushPostponed();
    }
    this.initialized = true;
  }

  /**
   * Initialize global dataLayer variable
   */
  private initDataLayer(): void {
    if (isPlatformBrowser(this.platformId)) {
      (window as any).dataLayer = [];
    }
  }

  /**
   * Provide dataLayer (or noop one for server platform)
   */
  private getDataLayer(): {[key: string]: any}[] {
    return isPlatformBrowser(this.platformId) ? (window as any).dataLayer : [];
  }

  /**
   * Push all postponed pushes to dataLayer
   */
  private pushPostponed(): void {
    this.postponedPushes.forEach(obj => this.pushToDataLayer(obj));
    this.postponedPushes = [];
  }

  /**
   * Push object do dataLayer
   * @param obj - object to be pushed
   */
  private pushToDataLayer(obj: {[key: string]: any}): void {
    if (this.isEnabled !== false) {
      const dataLayer = this.getDataLayer();
      dataLayer.push(obj);
    }
  }

  /**
   * Append necessary elements to DOM
   */
  private appendGtmTags(): void {
    if (isPlatformBrowser(this.platformId)) {
      (this.domDocument.head as any).prepend(this.buildGtmScript(appConfig.GtmId));
      (this.domDocument.body as any).prepend(this.buildGtmIframe(appConfig.GtmId));
      (this.domDocument.head as any).prepend(this.buildGtmScriptCode(appConfig.GtmId));
      if(!!this.gtagManagerEtab) {
        (this.domDocument.head as any).prepend(this.buildGtmScript(this.gtagManagerEtab));
        (this.domDocument.body as any).prepend(this.buildGtmIframe(this.gtagManagerEtab));
        (this.domDocument.head as any).prepend(this.buildGtmScriptCode(this.gtagManagerEtab));
      }
    }
  }

  /**
   * Create GTM initialization script
   */
  private buildGtmScript(gmtId: string): HTMLElement {
    const gtmScript = this.domDocument.createElement('script');
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gmtId}`;
    gtmScript.async = true;
    return gtmScript;
  }

  /**
   * Create GTM noscript iframe
   */
  private buildGtmIframe(gmtId: string): HTMLElement {
    const gtmIframe = this.domDocument.createElement('iframe');
    gtmIframe.setAttribute('src', `https://www.googletagmanager.com/ns.html?id=${gmtId}`);
    gtmIframe.style.width = '0';
    gtmIframe.style.height = '0';
    gtmIframe.style.display = 'none';
    gtmIframe.style.visibility = 'hidden';
    const gtmNoscript = this.domDocument.createElement('noscript');
    gtmNoscript.innerHTML = gtmIframe.outerHTML;
    return gtmNoscript;
  }

  /**
   * Create GTM script code
   */
  private buildGtmScriptCode(gmtId: string): HTMLElement {
    const gtmScriptCode = this.domDocument.createElement('script');
    gtmScriptCode.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gmtId}')`;
    return gtmScriptCode;
  }

}